import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Skeleton, Text } from '@mantine/core';
import { IconBriefcase, IconChartBar, IconMessage, IconShoppingCart, IconTag } from '@tabler/icons-react';
import PageTitle from '@/components/common/PageTitle';
import HomePanel, { type PanelBlock } from '@/components/app/home/HomePanel';
import HomeSection from '@/components/app/home/HomeSection';
import AddEmployeesCard from '@/components/app/home/AddEmployeesCard';
import StartTradingCard from '@/components/app/home/StartTradingCard';
import FirstStepsCard from '@/components/app/home/FirstStepsCard';
import AttentionSection from '@/components/app/home/AttentionSection';
import UpcomingDeadlines from '@/components/app/home/UpcomingDeadlines';
import BestOffersList from '@/components/app/home/BestOffersList';
import AgentMarketSummary from '@/components/app/home/AgentMarketSummary';
import { useChat } from '@/chat/useChat';
import { useProfile } from '@/hooks/useProfile';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useIsAgent, useIsManager } from '@/hooks/useRoles';
import { useAllDocuments } from '@/hooks/useAllDocuments';
import { useEmployeeInvitations } from '@/hooks/useEmployeeInvitations';
import { useBestOffers } from '@/hooks/useBestOffers';
import { useAgentMarket } from '@/hooks/useAgentAnalytics';
import { useBuyTree } from '@/hooks/useBuy';
import { useSaleTree } from '@/hooks/useSell';
import { useDealSideTotals, useDealsOverview } from '@/hooks/useDealsOverview';
import { useNotificationList } from '@/hooks/useNotifications';
import { flattenBuyTree } from '@/lib/buyTree';
import { flattenSaleTree } from '@/lib/sellTree';
import { marketTotals, topMarketCrops } from '@/lib/agentDashboard';
import { attentionView, buildAttention, dealsSummary, upcomingDeadlines } from '@/lib/dashboard';
import { formatMoney, formatOrgName } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import type { ChatConnectionStatus } from '@/types/chat';
import classes from '@/components/app/home/home.module.css';

const PANEL_ICON = { size: 24, stroke: 1.7 } as const;
const CHAT_CONNECTING: ReadonlySet<ChatConnectionStatus> = new Set(['idle', 'connecting', 'syncing']);

// «Главная» кабинета: приветствие, панель разделов с живыми числами, ниже две колонки — дела слева,
// лучшие предложения (у агента — рынок) справа. До аттестации вместо панели — онбординг. Тонкая:
// расчёты — в lib/dashboard и lib/agentDashboard.
export default function AppHome() {
  const { t } = useTranslation();
  const { data: profile } = useProfile();
  const { rooms: chatRooms, unreadTotal: chatUnread, status: chatStatus } = useChat();
  // Активные чаты — не только для чтения (по завершённым сделкам).
  const activeChats = chatRooms.filter((room) => room.writable).length;
  // Пока чат подключается, число неизвестно: показывать «0» нельзя.
  const chatsLoading = chatRooms.length === 0 && CHAT_CONNECTING.has(chatStatus);
  const chatsFailed = chatRooms.length === 0 && chatStatus === 'error';
  const buyTree = useBuyTree();
  const saleTree = useSaleTree();
  const overview = useDealsOverview();
  const sideTotals = useDealSideTotals();
  const notifications = useNotificationList();
  const bestOffers = useBestOffers();
  const market = useAgentMarket();

  // «Добавьте сотрудников»: только владельцу-менеджеру аттестованной организации, у которой ещё нет
  // сотрудников (ни принятых, ни ожидающих приглашений). Запрос не шлём, пока условие не выполнено, —
  // сервер иначе ответит 406.
  const isManager = useIsManager();
  const isAgent = useIsAgent();
  const orgAttested = profile?.isVerified === true;
  // Управлять штатом может менеджер аттестованной организации или менеджер агентской (агенту аттестация
  // не требуется). От этого зависит и запрос приглашений, и напоминание «Добавьте сотрудников».
  const canManageEmployees = isManager && (orgAttested || isAgent);
  const invitations = useEmployeeInvitations(canManageEmployees);
  // Признак сельхозтоваропроизводителя — из документов аккредитации; грузим только когда он нужен
  // (аттестованная организация, не агент — агент листинги не публикует).
  const startTradingDocs = useAllDocuments(orgAttested && !isAgent);
  const hasEmployees = (invitations.data ?? []).some(
    (invitation) => invitation.status === 'ACCEPTED' || invitation.status === 'PENDING',
  );

  // Онбординг активен → панель скрыта (до аттестации торговать нельзя), вместо неё «Первые шаги».
  const onboarding = useOnboarding();

  // «Добавьте сотрудников» можно закрыть крестиком. Решение запоминаем для пользователя в localStorage,
  // чтобы напоминание не всплывало снова. Оно и так исчезает после первого приглашённого сотрудника.
  const dismissKey = profile?.id != null ? `data1861-add-employees-dismissed:${profile.id}` : null;
  const [addEmployeesDismissed, setAddEmployeesDismissed] = useState(false);
  useEffect(() => {
    setAddEmployeesDismissed(dismissKey != null && localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);
  const dismissAddEmployees = (): void => {
    if (dismissKey != null) {
      localStorage.setItem(dismissKey, '1');
    }
    setAddEmployeesDismissed(true);
  };
  const showAddEmployees = canManageEmployees && invitations.isSuccess && !hasEmployees && !addEmployeesDismissed;

  const greetingName = profile?.firstName || formatOrgName(profile?.name) || '';
  const buyCount = flattenBuyTree(buyTree.data).length;
  const sellCount = flattenSaleTree(saleTree.data).length;
  // Сбой загрузки нельзя выдавать за ноль: человек с прайсами получил бы призыв создать новый.
  const buyFailed = buyTree.isError && buyTree.data == null;
  const sellFailed = saleTree.isError && saleTree.data == null;
  // «Уже торговал» учитывает и завершённые сделки, поэтому берётся из общего числа по сторонам, а не из
  // сводки «Главной» (в ней только активные). Пока число неизвестно — undefined.
  const hasTraded =
    sideTotals.sale != null && sideTotals.purchase != null ? sideTotals.sale + sideTotals.purchase > 0 : undefined;
  // Напоминание «создайте первый листинг» аттестованной организации без нужного листинга: сельхоз-
  // товаропроизводителю — предложение о продаже, остальным — прайс-лист. Агенту не показываем. Ждём
  // деревья, сделки и признак производителя: без них напоминание мигало бы или звало создать созданное.
  const startTradingMode: 'sell' | 'buy' | null =
    orgAttested && !isAgent && !onboarding.active && buyTree.data != null && saleTree.data != null && hasTraded != null && startTradingDocs.isSuccess
      ? startTradingDocs.data.agriculturalProducer
        ? sellCount === 0
          ? 'sell'
          : null
        : buyCount === 0
          ? 'buy'
          : null
      : null;

  const nowMs = Date.now();
  // Выборка активных сделок полная — приостановку сделки вне её можно считать завершённой и не показывать.
  const dealsComplete = !overview.isLoading && !overview.isError && !overview.partial && !overview.truncated;
  const attention = buildAttention(overview.deals, notifications.data, isAgent, nowMs, dealsComplete);
  const attentionRows = attentionView(attention);
  const deadlines = upcomingDeadlines(overview.deals, nowMs, isAgent);
  const summary = dealsSummary(overview.deals, isAgent);
  const dealsCount = overview.truncated ? overview.total : summary.count;

  const attentionLoading = overview.isLoading || notifications.isLoading;
  const notificationsFailed = notifications.isError && notifications.data == null;
  const attentionPartial = !overview.isError && (overview.partial || notificationsFailed);
  const retryAttention = (): void => {
    overview.retry();
    if (notificationsFailed) {
      void notifications.refetch();
    }
  };
  // Строка под приветствием: сколько решений ждут человека. Место держим, чтобы блоки не прыгали.
  const statusLine =
    !attentionLoading && dealsComplete && !notificationsFailed && attentionRows.mineTotal > 0
      ? t('app.home.status', { count: attentionRows.mineTotal })
      : null;

  // Число встречных предложений по сторонам: в «Купить» — предложения продавцов к моим прайсам,
  // в «Продать» — выгодные предложения покупателей к моим предложениям.
  const sellerOffers = bestOffers.data.filter((offer) => offer.side === 'BUY').length;
  const buyerOffers = bestOffers.data.filter((offer) => offer.side === 'SELL').length;
  const money = (amount: number | undefined, key: string): string | null =>
    amount != null && amount > 0 ? t(key, { amount: formatMoney(amount) }) : null;
  const marketFailed = market.isError && market.data == null;
  const marketSum = marketTotals(market.data?.crops ?? []);

  const dealsBlock: PanelBlock = {
    key: 'deals',
    to: ROUTES.APP_DEALS,
    icon: <IconBriefcase {...PANEL_ICON} />,
    title: t('app.nav.deals'),
    loading: overview.isLoading,
    failed: overview.isError || overview.partial,
    // Полная выборка — число сделок в работе (продавцу без шагов закрытия); при неполной — число от сервера.
    value: dealsCount,
    unit: t('app.home.unit.deals', { count: dealsCount }),
    // При неполной выборке (больше 50 активных сделок стороны) суммы и раскладка были бы неверны — скрываем.
    lines: overview.truncated
      ? []
      : isAgent
        ? [money(summary.total, 'app.home.panel.amount')]
        : [money(summary.buying, 'app.home.panel.buying'), money(summary.selling, 'app.home.panel.selling')],
    bar: overview.truncated
      ? undefined
      : { mine: summary.mine, late: summary.late, mineLate: summary.mineLate, others: summary.others },
  };
  const chatsBlock: PanelBlock = {
    key: 'chats',
    to: ROUTES.APP_CHAT,
    icon: <IconMessage {...PANEL_ICON} />,
    title: t('app.nav.chat'),
    loading: chatsLoading,
    failed: chatsFailed,
    value: activeChats,
    unit: t('app.home.unit.chats', { count: activeChats }),
    lines: [chatUnread > 0 ? t('app.home.panel.newChats', { count: chatUnread }) : null],
  };
  const panelBlocks: PanelBlock[] = isAgent
    ? [
        {
          key: 'market',
          to: ROUTES.APP_AGENT_DASHBOARD,
          icon: <IconChartBar {...PANEL_ICON} />,
          title: t('app.nav.market'),
          loading: market.isLoading,
          failed: marketFailed,
          value: marketSum.sell,
          unit: t('app.home.unit.sellOffers', { count: marketSum.sell }),
          lines: [marketSum.buy > 0 ? t('app.home.panel.marketBuy', { count: marketSum.buy }) : null],
        },
        dealsBlock,
        chatsBlock,
      ]
    : [
        {
          key: 'buy',
          to: ROUTES.APP_BUY,
          icon: <IconShoppingCart {...PANEL_ICON} />,
          title: t('app.nav.buy'),
          loading: buyTree.isLoading,
          failed: buyFailed,
          value: buyCount,
          unit: t('app.home.unit.prices', { count: buyCount }),
          emptyCta: t('app.home.tile.createPrice'),
          lines: [sellerOffers > 0 ? t('app.home.panel.sellerOffers', { count: sellerOffers }) : null],
        },
        {
          key: 'sell',
          to: ROUTES.APP_SELL,
          icon: <IconTag {...PANEL_ICON} />,
          title: t('app.nav.sell'),
          loading: saleTree.isLoading,
          failed: sellFailed,
          value: sellCount,
          unit: t('app.home.unit.offers', { count: sellCount }),
          emptyCta: t('app.home.tile.createOffer'),
          lines: [buyerOffers > 0 ? t('app.home.panel.goodOffers', { count: buyerOffers }) : null],
        },
        dealsBlock,
        chatsBlock,
      ];

  // Правая колонка: у агента — рынок, у остальных — лучшие предложения. Пустую колонку не держим:
  // тогда дела занимают всю ширину.
  const marketCrops = topMarketCrops(market.data?.crops ?? []);
  const rightColumn = isAgent ? (
    market.isLoading || marketCrops.length > 0 ? (
      <AgentMarketSummary crops={marketCrops} loading={market.isLoading} />
    ) : null
  ) : bestOffers.isLoading && !onboarding.active ? (
    <HomeSection title={t('app.home.bestOffers.title')}>
      <Skeleton h={240} mt="md" aria-busy />
    </HomeSection>
  ) : bestOffers.data.length > 0 ? (
    <HomeSection title={t('app.home.bestOffers.title')}>
      <BestOffersList items={bestOffers.data} />
    </HomeSection>
  ) : null;

  const deadlinesSection =
    overview.isLoading && !onboarding.active ? null : deadlines.length > 0 ? (
      <HomeSection title={t('app.home.deadlines.title')}>
        <UpcomingDeadlines items={deadlines} />
      </HomeSection>
    ) : null;

  const lead = onboarding.active ? t('app.home.firstSteps.subtitle') : statusLine;

  return (
    <Container size="lg" py="xl">
      <PageTitle>{greetingName ? t('app.home.greeting', { name: greetingName }) : t('app.home.title')}</PageTitle>
      <Box mih={24} mt={6}>
        {lead ? (
          <Text c="gray.7" size="md">
            {lead}
          </Text>
        ) : null}
      </Box>

      {onboarding.pending ? <Skeleton h={150} mt={20} aria-busy /> : null}
      {onboarding.active ? (
        <Box mt="md">
          <FirstStepsCard />
        </Box>
      ) : null}
      {!onboarding.active && !onboarding.pending ? (
        <Box mt={20}>
          <HomePanel blocks={panelBlocks} />
        </Box>
      ) : null}

      {startTradingMode ? (
        <Box mt={24}>
          <StartTradingCard mode={startTradingMode} returning={hasTraded === true} />
        </Box>
      ) : null}
      {showAddEmployees ? (
        <Box mt={24}>
          <AddEmployeesCard onClose={dismissAddEmployees} />
        </Box>
      ) : null}

      <div className={`${classes.columns}${rightColumn ? '' : ` ${classes.single}`}`} style={{ marginTop: 28 }}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          <AttentionSection
            view={attentionRows}
            loading={attentionLoading}
            failed={overview.isError}
            partial={attentionPartial}
            hasDeals={summary.count > 0 && !overview.truncated}
            retrying={overview.isRefetching || notifications.isRefetching}
            onRetry={retryAttention}
          />
          {deadlinesSection}
        </Box>
        {rightColumn}
      </div>
    </Container>
  );
}
